import {
  Badge,
  BadgeButton,
  Box,
  Button,
  Checkbox,
  Column,
  Dialog,
  IconButton,
  PlusIcon,
  Row,
  Text,
  useDisclosure,
} from "@hightouch/htui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import type {
  FMColumnConfig,
  FeatureMatrixFormValues,
} from "src/pages/decision-engines/configuration/feature-matrices/types";
import {
  ALL_AGGREGATION_OPTIONS,
  SHOULD_DIRTY_OPTION,
  computeVariantsForColumn,
  type ComputedVariant,
} from "src/pages/decision-engines/configuration/feature-matrices/utils";
import { inferFilterName } from "./filter-components";

const AGG_LABELS = Object.fromEntries(
  ALL_AGGREGATION_OPTIONS.map((o) => [o.value, o.label]),
);

function formatTimeWindow(tw: number): string {
  if (!tw || tw === -1) return "all time";
  return `${tw}d`;
}

export function InsightsVariantsPopover({
  modelKey,
  columnName,
  config,
  requiresAggregation,
}: {
  modelKey: string;
  columnName: string;
  config: FMColumnConfig;
  requiresAggregation: boolean;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { watch, setValue } = useFormContext<FeatureMatrixFormValues>();

  const [variants, setVariants] = useState<ComputedVariant[]>([]);
  const configRef = useRef(config);
  configRef.current = config;

  // configKey serializes the fields that affect variants so the effect only
  // re-runs when one of them actually changes.
  const configKey = useMemo(
    () =>
      JSON.stringify({
        agg: config.aggregates,
        tw: config.timeWindowDays,
        f: config.filters?.map((f) => f.conditions),
        u: config.includeUnfiltered,
      }),
    [
      config.aggregates,
      config.timeWindowDays,
      config.filters,
      config.includeUnfiltered,
    ],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    let canceled = false;
    computeVariantsForColumn(columnName, configRef.current, requiresAggregation)
      .then((result) => {
        if (!canceled) setVariants(result);
      })
      .catch((err) => {
        console.error("Failed to compute insight variants:", err);
        if (!canceled) setVariants([]);
      });
    return () => {
      canceled = true;
    };
  }, [columnName, configKey, requiresAggregation]);

  const excludedVariants: string[] =
    watch(`relationships.${modelKey}.${columnName}.insightsExcludedVariants`) ??
    [];
  const excludedSet = new Set(excludedVariants);

  if (variants.length <= 1) {
    return null;
  }

  const enabledCount = variants.filter(
    (v) => !excludedSet.has(v.backendName),
  ).length;
  const hasExclusions = enabledCount < variants.length;

  return (
    <>
      {hasExclusions ? (
        <Box as={BadgeButton} onClick={onOpen}>
          {enabledCount}/{variants.length}
        </Box>
      ) : (
        <IconButton
          icon={PlusIcon}
          aria-label="Configure insights variants"
          size="sm"
          variant="secondary"
          onClick={onOpen}
        />
      )}
      {isOpen && (
        <InsightsVariantsDialog
          columnName={columnName}
          variants={variants}
          excludedVariants={excludedVariants}
          onSave={(newExcluded) => {
            setValue(
              `relationships.${modelKey}.${columnName}.insightsExcludedVariants`,
              newExcluded,
              SHOULD_DIRTY_OPTION,
            );
            onClose();
          }}
          onClose={onClose}
        />
      )}
    </>
  );
}

function InsightsVariantsDialog({
  columnName,
  variants,
  excludedVariants,
  onSave,
  onClose,
}: {
  columnName: string;
  variants: ComputedVariant[];
  excludedVariants: string[];
  onSave: (excluded: string[]) => void;
  onClose: () => void;
}) {
  const [localExcluded, setLocalExcluded] = useState<Set<string>>(
    () => new Set(excludedVariants),
  );

  const toggleVariant = useCallback((backendName: string) => {
    setLocalExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(backendName)) {
        next.delete(backendName);
      } else {
        next.add(backendName);
      }
      return next;
    });
  }, []);

  const handleSave = () => {
    onSave(Array.from(localExcluded));
  };

  return (
    <Dialog
      isOpen
      title={`Insights variants for ${columnName}`}
      variant="form"
      width="lg"
      actions={
        <Row gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Row>
      }
      onClose={onClose}
    >
      <Column gap={3}>
        <Text size="sm" color="text.secondary">
          Select which feature variants are included in insights breakdowns.
        </Text>
        <Column gap={1} maxHeight="400px" overflow="auto">
          {variants.map((variant) => (
            <Row key={variant.backendName} align="center" gap={2} py={1} px={1}>
              <Checkbox
                isChecked={!localExcluded.has(variant.backendName)}
                onChange={() => toggleVariant(variant.backendName)}
                aria-label={`Include ${variant.backendName} in insights`}
              />
              <Row gap={1.5} align="center" flexWrap="wrap">
                <Badge size="sm">
                  {AGG_LABELS[variant.aggregation] ?? variant.aggregation}
                </Badge>
                <Badge size="sm">{formatTimeWindow(variant.timeWindow)}</Badge>
                {variant.filterConditions && (
                  <Badge size="sm" variant="subtle">
                    {inferFilterName(variant.filterConditions)}
                  </Badge>
                )}
              </Row>
            </Row>
          ))}
        </Column>
      </Column>
    </Dialog>
  );
}
